import type { ProjectSummary } from "@/lib/types";

export const UNKNOWN_CLIENT_KEY = "__unknown_client__";
export const UNKNOWN_LOCATION_KEY = "__unknown_location__";

export type ProjectSelection = {
  clientKey: string;
  locationKey: string;
  projectId: string;
};

export type ProjectOption = {
  value: string;
  label: string;
};

export type ClientOption = {
  key: string;
  label: string;
};

export type LocationOption = {
  key: string;
  label: string;
};

type SelectionOptions = {
  filter?: (project: ProjectSummary) => boolean;
  projectLabel?: (project: ProjectSummary) => string;
  preserveEmptyProject?: boolean;
};

const defaultProjectLabel = (project: ProjectSummary): string =>
  (project.project_name || "").trim() || "Untitled project";

export function normalizeClientKey(project: ProjectSummary): string {
  const raw = typeof project.client_name === "string" ? project.client_name.trim() : "";
  return raw ? raw.toLowerCase() : UNKNOWN_CLIENT_KEY;
}

export function normalizeLocationKey(project: ProjectSummary): string {
  const cityRaw = typeof project.city === "string" ? project.city.trim() : "";
  const stateRaw = typeof project.state === "string" ? project.state.trim() : "";
  const city = cityRaw ? cityRaw.toUpperCase() : "";
  const state = stateRaw ? stateRaw.toUpperCase() : "";
  if (!city && !state) return UNKNOWN_LOCATION_KEY;
  return `${city}||${state}`;
}

export function getClientLabel(project: ProjectSummary): string {
  const raw = typeof project.client_name === "string" ? project.client_name.trim() : "";
  return raw ? raw : "Unknown client";
}

export function getLocationLabel(project: ProjectSummary): string {
  const city = typeof project.city === "string" ? project.city.trim().toUpperCase() : "";
  const state = typeof project.state === "string" ? project.state.trim().toUpperCase() : "";
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  return "Unknown location";
}

export function buildClientOptions(
  projects: ProjectSummary[],
  options?: SelectionOptions,
): ClientOption[] {
  const filter = options?.filter;
  const map = new Map<string, ClientOption>();
  for (const project of projects) {
    if (filter && !filter(project)) continue;
    const key = normalizeClientKey(project);
    if (!map.has(key)) {
      map.set(key, { key, label: getClientLabel(project) });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function buildLocationOptions(
  projects: ProjectSummary[],
  clientKey: string,
  options?: SelectionOptions,
): LocationOption[] {
  if (!clientKey) return [];
  const filter = options?.filter;
  const map = new Map<string, LocationOption>();
  for (const project of projects) {
    if (filter && !filter(project)) continue;
    if (normalizeClientKey(project) !== clientKey) continue;
    const key = normalizeLocationKey(project);
    if (!map.has(key)) {
      map.set(key, { key, label: getLocationLabel(project) });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function filterProjectsForSelection(
  projects: ProjectSummary[],
  selection: { clientKey: string; locationKey: string },
  options?: SelectionOptions,
): ProjectSummary[] {
  const filter = options?.filter;
  if (!selection.clientKey || !selection.locationKey) return [];
  return projects.filter((project) => {
    if (filter && !filter(project)) return false;
    return (
      normalizeClientKey(project) === selection.clientKey &&
      normalizeLocationKey(project) === selection.locationKey
    );
  });
}

export function buildProjectOptions(
  projects: ProjectSummary[],
  options?: SelectionOptions,
): ProjectOption[] {
  const labelFn = options?.projectLabel ?? defaultProjectLabel;
  return projects
    .slice()
    .sort((a, b) => labelFn(a).localeCompare(labelFn(b)))
    .map((project) => ({ value: project.id, label: labelFn(project) }));
}

export function normalizeProjectSelection(
  projects: ProjectSummary[],
  selection: ProjectSelection,
  options?: SelectionOptions,
): ProjectSelection {
  if (!projects.length) {
    return { clientKey: "", locationKey: "", projectId: "" };
  }

  const clientOptions = buildClientOptions(projects, options);
  const clientKeys = clientOptions.map((c) => c.key);
  const nextClient =
    selection.clientKey && clientKeys.includes(selection.clientKey) ? selection.clientKey : clientKeys[0] || "";

  const locationOptions = buildLocationOptions(projects, nextClient, options);
  const locationKeys = locationOptions.map((l) => l.key);
  const nextLocation =
    selection.locationKey && locationKeys.includes(selection.locationKey)
      ? selection.locationKey
      : locationKeys[0] || "";

  const projectsForLocation = filterProjectsForSelection(
    projects,
    { clientKey: nextClient, locationKey: nextLocation },
    options,
  );
  const projectOptions = buildProjectOptions(projectsForLocation, options);
  const projectIds = projectOptions.map((p) => p.value);
  const nextProject = (() => {
    if (!selection.projectId && options?.preserveEmptyProject) return "";
    if (selection.projectId && projectIds.includes(selection.projectId)) return selection.projectId;
    return projectIds[0] || "";
  })();

  return { clientKey: nextClient, locationKey: nextLocation, projectId: nextProject };
}

export function selectionFromProjectId(
  projects: ProjectSummary[],
  projectId: string,
): ProjectSelection | null {
  if (!projectId) return null;
  const project = projects.find((p) => p.id === projectId);
  if (!project) return null;
  return {
    clientKey: normalizeClientKey(project),
    locationKey: normalizeLocationKey(project),
    projectId: project.id,
  };
}

